import { useLoadScript } from "@react-google-maps/api";
import { PenLineIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getUser } from "../../../Utils/auth";
import axios from "../../../Utils/axiosInstance";
import { s3Upload } from "../../../Utils/s3Upload";
import AppLoader from "../../Common/AppLoader";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";
import RechargeModal from "../Recharge/RechargeModal";
import AddAddressModalAPI from "../Home/Components/AddAddressModalAPI";
import AddressSelectModal from "../Home/Components/AddressSelectModal";
import { assets } from "./../../../Assets/assets";
import DeliveryTime from "./Components/DeliveryTime";
import OtherRequirements from "./Components/OtherRequirements";
import OTPModal from "./Components/OTPModal";
import PriceAndQuantity from "./Components/PriceAndQuantity";
import QuestionnaireSection from "./Components/QuestionnaireSection";
import WalletModal from "./Components/WalletModal";
import { useAuth } from "../../Context/AuthContext";

const STORAGE_KEY = "postRequirementDraft";

const MobileCollapse = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4 lg:hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3"
      >
        <span className="text-sm font-semibold text-gray-700">{title}</span>

        <span className={`transition ${open ? "rotate-180" : ""}`}>▼</span>
      </button>

      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

const HowToUseSidebar = () => {
  const steps = [
    {
      title: "Select Product",
      desc: "Choose category, sub-category and product variety.",
    },
    {
      title: "Add Details",
      desc: "Fill MOQ, price range and requirements.",
    },
    {
      title: "Upload Media",
      desc: "Add images & videos for better supplier response.",
    },
    {
      title: "Submit Requirement",
      desc: "Pay ₹69 fee and receive verified quotations.",
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm lg:sticky lg:top-16 lg:max-h-[80vh] overflow-y-auto">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-sm font-bold text-gray-800">
          📘 How to Ask For A Quick Quotation
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Follow these steps to get the best quotes
        </p>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step, i) => (
          <div key={i} className="flex gap-3 items-start">
            {/* Step Number */}
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primaryColor text-white text-xs font-bold flex-shrink-0">
              {i + 1}
            </div>

            {/* Content */}
            <div>
              <p className="text-sm font-semibold text-gray-700">
                {step.title}
              </p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                {step.desc}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Divider */}
      <div className="my-4 h-px bg-gray-100" />

      {/* Extra Info */}
      <div className="bg-primaryColor/5 border border-primaryColor/20 rounded-lg p-3">
        <p className="text-xs text-gray-600">
          💡 Tip: More details = better price matches from suppliers.
        </p>
      </div>

      {/* Help */}
      <div className="mt-4 flex items-start gap-2">
        <div className="text-primaryColor text-lg">🎧</div>
        <p className="text-xs text-gray-500">
          Need help? Contact support anytime{" "}
          <a
            href="tel:+9108062177433"
            className="text-primaryColor hover:underline"
          >
            +9108062177433
          </a>
          .
        </p>
      </div>
    </div>
  );
};

const SkeletonTag = () => (
  <div className="h-9 w-28 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
);

const SkeletonRadio = () => (
  <div className="flex items-center gap-2">
    <div className="w-4 h-4 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
    <div className="h-4 w-20 rounded bg-gray-200 animate-pulse" />
  </div>
);

const EmptyState = ({ icon, title, subtitle }) => (
  <div className="flex flex-col items-center justify-center py-8 gap-2">
    <div className="w-12 h-12 rounded-full bg-primaryColor/10 flex items-center justify-center text-primaryColor text-xl">
      {icon}
    </div>
    <p className="text-sm font-medium text-gray-600">{title}</p>
    {subtitle && (
      <p className="text-xs text-gray-400 text-center">{subtitle}</p>
    )}
  </div>
);

const NoDataState = ({ label }) => (
  <div className="flex items-center gap-2 py-3 px-4 bg-amber-50 border border-amber-200 rounded-lg">
    <svg
      className="w-4 h-4 text-amber-500 flex-shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
    <p className="text-xs text-amber-700">
      No {label} available for this selection
    </p>
  </div>
);

const Tag = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 sm:px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-150 active:scale-95 ${active ? "bg-primaryColor text-white border-primaryColor shadow-sm shadow-primaryColor/30" : "bg-white text-gray-600 border-gray-300 hover:border-primaryColor hover:text-primaryColor"}`}
  >
    {label}
  </button>
);

const SectionHeader = ({ title }) => (
  <div className="flex items-center gap-3 mb-3">
    <span className="text-sm font-semibold text-gray-700">{title}</span>
    <div className="flex-1 h-px bg-gray-200" />
  </div>
);

const StepIndicator = ({ currentStep }) => {
  const steps = ["Select Product", "Upload", "Submit"];
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {steps.map((label, i) => {
        const isActive = i === currentStep;
        const isDone = i < currentStep;
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <div
                className={`h-px w-20 sm:w-32 transition-colors duration-300 ${isDone ? "bg-primaryColor" : "bg-gray-300"}`}
              />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-3 h-3 rounded-full border-2 transition-all duration-300 ${isDone ? "bg-primaryColor border-primaryColor" : isActive ? "bg-white border-primaryColor ring-2 ring-primaryColor/20" : "bg-white border-gray-300"}`}
              />
              <span
                className={`text-xs font-medium whitespace-nowrap ${isActive || isDone ? "text-primaryColor" : "text-gray-400"}`}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && <div className="px-5 pb-5 pt-1 bg-white">{children}</div>}
    </div>
  );
};

const UploadBox = ({ type, onChange, disabled, count, max }) => {
  const inputRef = useRef();
  return (
    <label
      className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-5 cursor-pointer transition-all duration-150 ${disabled ? "opacity-40 cursor-not-allowed border-gray-300 bg-gray-50" : "border-primaryColor/40 hover:bg-primaryColor/5 hover:border-primaryColor"}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={type === "image" ? "image/*" : "video/*"}
        multiple={type === "image" || type === "video"}
        hidden
        onChange={onChange}
        disabled={disabled}
      />
      <div className="w-10 h-10 rounded-full bg-primaryColor/10 flex items-center justify-center">
        <svg
          className="w-5 h-5 text-primaryColor"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-gray-700">
          {type === "image" ? "Image" : "Video"}
        </p>
        <p className="text-[10px] text-gray-400 mt-0.5">
          {type === "image"
            ? "Supported – PNG, JPEG, JPG, WEBP, HEIC, PDF | max 10 MB"
            : "Supported – MP4, MOV, WEBM, AVI, MKV | max 25 MB"}
        </p>
      </div>
      <span
        className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${count >= max ? "bg-red-100 text-red-600" : "bg-primaryColor/10 text-primaryColor"}`}
      >
        {count}/{max} uploaded
      </span>
    </label>
  );
};

const ImagePreview = ({ files, onRemove }) => {
  return (
    <div className="flex gap-3 mt-3 flex-wrap">
      {/* Uploaded Images */}
      {files.map((file, i) => {
        const url = URL.createObjectURL(file);
        return (
          <div key={i} className="relative group">
            <img
              src={url}
              alt={`preview-${i}`}
              className="w-20 h-20 object-cover rounded-xl border-2 border-primaryColor/20"
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
            >
              ✕
            </button>
          </div>
        );
      })}

      {/* Empty Slots (MAX 3) */}
      {Array(3 - files.length)
        .fill(0)
        .map((_, i) => (
          <div
            key={`empty-${i}`}
            className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center"
          >
            <svg
              className="w-5 h-5 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
          </div>
        ))}
    </div>
  );
};

const VideoPreview = ({ files, onRemove }) => {
  return (
    <div className="flex gap-3 mt-3 flex-wrap">
      {/* Uploaded videos */}
      {files.map((file, i) => {
        const url = URL.createObjectURL(file);
        return (
          <div key={i} className="relative group">
            <video
              src={url}
              className="w-20 h-20 rounded-xl object-cover border-2 border-primaryColor/20"
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
            >
              ✕
            </button>
          </div>
        );
      })}

      {/* Empty slots (MAX 2) */}
      {Array(2 - files.length)
        .fill(0)
        .map((_, i) => (
          <div
            key={`empty-${i}`}
            className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center"
          >
            <svg
              className="w-5 h-5 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 10l4.553-2.069A1 1 0 0121 8.868v6.264a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
        ))}
    </div>
  );
};

const SidebarCard = ({
  illustration,
  subtitle,
  buttonLabel,
  onButtonClick,
}) => (
  <div className="bg-white border border-gray-200 rounded-md p-5 flex flex-col items-center text-center gap-3 shadow-sm">
    <div className="w-full flex justify-center">{illustration}</div>
    <p className="text-sm text-gray-600 leading-relaxed">{subtitle}</p>
    <button
      type="button"
      onClick={onButtonClick}
      className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95 bg-primaryColor text-white hover:bg-primaryColor/90 shadow-sm shadow-primaryColor/30"
    >
      {buttonLabel}
    </button>
  </div>
);

const LoginIllustration = () => <img src={assets.loginvector} alt="" />;

const LocationIllustration = () => <img src={assets.locationvector} alt="" />;

const ProductSummaryPanel = ({
  selected,
  categories,
  subCategories,
  products,
  varieties,
  isCustomOrder,
  customVarietyName,
}) => {
  const cat = categories.find((c) => c._id === selected.category);
  const sub = subCategories.find((c) => c._id === selected.subCategory);
  const prod = products.find((c) => c._id === selected.product);
  const vari = varieties.find((c) => c._id === selected.variety);
  return (
    <div className="bg-white border border-gray-200 rounded-md p-5 shadow-sm sticky top-16">
      <h3 className="text-sm font-bold text-gray-700 mb-4 pb-3 border-b border-gray-100">
        Your Selection
      </h3>
      {!cat ? (
        <div className="flex flex-col items-center justify-center py-6 gap-3 text-center">
          <div className="w-14 h-14 rounded-full bg-primaryColor/10 flex items-center justify-center">
            <svg
              className="w-7 h-7 text-primaryColor/50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500">
              No product selected
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              Select a category to get started
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {[
            { label: "Category", value: cat?.name },
            { label: "Sub Category", value: sub?.name },
            { label: "Product", value: prod?.name },
            {
              label: "Product Variety",
              value: isCustomOrder ? customVarietyName : vari?.varietyName,
            },
          ].map(({ label, value }) =>
            value ? (
              <div key={label} className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  {label}
                </span>
                <span className="text-sm whitespace-pre-wrap font-semibold text-primaryColor">
                  {value}
                </span>
              </div>
            ) : null,
          )}
        </div>
      )}
      {prod && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-2">
            Product Name
          </p>
          <p className="text-sm font-bold text-gray-800">{prod?.name || "—"}</p>
        </div>
      )}
    </div>
  );
};

const SelectedVarietyCard = ({ variety }) => {
  if (!variety) return null;

  return (
    <div className="flex items-center gap-4 bg-primaryColor/5 border border-primaryColor/20 rounded-xl p-4">
      <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden border border-primaryColor/20 bg-white">
        {variety.img ? (
          <img
            src={variety.img}
            alt={variety.varietyName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-2xl">
            🌶️
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-primaryColor truncate">
          {variety.varietyName}
        </p>
        <div className="flex flex-wrap gap-2 mt-2">
          {variety.grade && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#EAF3DE] text-[#3B6D11]">
              ✓ {variety.grade}
            </span>
          )}
          {variety.slabAdjustedMOQ != null && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#fce8ed] text-primaryColor">
              Reduced MOQ: {variety.slabAdjustedMOQ} {variety.standardizedUNIT}
            </span>
          )}
          {/* {variety.floor_price != null && variety.cap_price != null && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
              ₹{variety.floor_price} – ₹{variety.cap_price} / {variety.standardizedUNIT}
            </span>
          )} */}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PostRequirement = ({ offerId }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });
  const { user: User } = useAuth();

  const [appLoading, setAppLoading] = useState({
    uploading: false,
    submitting: false,
    creatingOrder: false,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const PLATFORM_FEE = 69;
  const [step, setStep] = useState(0);
  const { id, ...user } = getUser();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});
  const [selected, setSelected] = useState({
    category: searchParams.get("category") || null,
    subCategory: searchParams.get("subCategory") || null,
    product: searchParams.get("product") || null,
    variety: searchParams.get("variety") || null,
  });
  const isLocked = searchParams.get("locked") === "true";
  const [customVarietyName, setCustomVarietyName] = useState("");

  // If user manually changes URL params, update selected state accordingly
  const [isCustomOrder, setIsCustomOrder] = useState(false);
  const [isDeclarationAccepted, setIsDeclarationAccepted] = useState(false);

  // Questionnaire
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [answers, setAnswers] = useState({});

  // otp
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  // Other requirements (max 3)
  const [otherRequirements, setOtherRequirements] = useState([]);

  // Sample requested
  const [sampleRequested, setSampleRequested] = useState("No");

  // Price & Quantity
  const [priceAndQuantity, setPriceAndQuantity] = useState({
    MOQ: "",
    currency: "INR",
    expectedPriceRange: { min: "", max: "" },
  });

  // Delivery time
  const [deliveryTime, setDeliveryTime] = useState({
    fromDate: "",
    toDate: "",
  });

  // Address
  const [selectedAddressId, setSelectedAddressId] = useState("");

  // Wallet
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);

  // Media
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Addresses
  const [addresses, setAddresses] = useState([]);
  const [defaultAddress, setDefaultAddress] = useState(null);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const [addressType, setAddressType] = useState("Office");
  const [editAddress, setEditAddress] = useState(null);
  const [isRestored, setIsRestored] = useState(false);

  const lockScroll =
    appLoading.uploading ||
    appLoading.submitting ||
    appLoading.creatingOrder ||
    showOTPModal ||
    showWalletModal ||
    showAddressModal ||
    showAddModal;

  // Hook
  useLockBodyScroll(lockScroll);

  // ─── Fetch Categories ─────────────────────────────────────────────────────
  useEffect(() => {
    fetchCategories();
    fetchWallet();
    fetchAddresses();
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);

        setSelected({
          category: parsed?.selected?.category || null,
          subCategory: parsed?.selected?.subCategory || null,
          product: parsed?.selected?.product || null,
          variety: parsed?.selected?.variety || null,
        });
        setCustomVarietyName(parsed.customVarietyName || "");
        setIsCustomOrder(parsed.isCustomOrder || false);
        setAnswers(parsed.answers || {});
        setOtherRequirements(parsed.otherRequirements || []);
        setPriceAndQuantity(
          parsed.priceAndQuantity || {
            MOQ: "",
            currency: "INR",
            expectedPriceRange: { min: "", max: "" },
          },
        );
        setDeliveryTime(parsed.deliveryTime || { fromDate: "", toDate: "" });
        setSelectedAddressId(parsed.selectedAddressId || "");
        setStep(parsed.step || 0);
      } catch (err) {
        console.error("Failed to restore draft");
      }
    }

    setIsRestored(true);
  }, []);

  useEffect(() => {
    if (!isRestored) return;

    const data = {
      selected,
      customVarietyName,
      isCustomOrder,
      answers,
      otherRequirements,
      priceAndQuantity,
      deliveryTime,
      selectedAddressId,
      step,
    };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [
    isRestored,
    selected,
    customVarietyName,
    isCustomOrder,
    answers,
    otherRequirements,
    priceAndQuantity,
    deliveryTime,
    selectedAddressId,
    step,
  ]);

  useEffect(() => {
    const hasParams = [...searchParams.keys()].length > 0;

    if (!hasParams) return;

    const newSelected = {
      category: searchParams.get("category") || null,
      subCategory: searchParams.get("subCategory") || null,
      product: searchParams.get("product") || null,
      variety: searchParams.get("variety") || null,
    };

    setSelected(newSelected);
  }, [searchParams]);

  // ─── NEW: Pre-select hierarchy from offer when offerId prop is provided ────
  useEffect(() => {
    if (!offerId) return;

    const fetchOfferAndPreselect = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/offer/get-offer-by-id/${offerId}`,
        );
        if (!data.success) return;

        const offer = data.data;
        const hierarchy = offer?.productHierarchy;

        // Pre-select all four hierarchy levels at once so the chain of
        // useEffects (sub → product → variety → questions) fires correctly
        setSelected({
          category: hierarchy?.category?._id ?? null,
          subCategory: hierarchy?.subCategory?._id ?? null,
          product: hierarchy?.product?._id ?? null,
          variety: hierarchy?.productVariety?._id ?? null,
        });

        // Pre-fill MOQ from offer quantity
        const moqValue = offer?.quantity?.weightInNumber;
        if (moqValue) {
          setPriceAndQuantity((prev) => ({
            ...prev,
            MOQ: String(moqValue),
          }));
        }
      } catch (err) {
        console.error("Failed to fetch offer for pre-selection:", err);
      }
    };

    fetchOfferAndPreselect();
  }, [offerId]);
  // ─────────────────────────────────────────────────────────────────────────────

  const fetchAddresses = async () => {
    try {
      const res = await axios.get("/address");
      const data = res?.data?.data || [];

      setAddresses(data);

      const def = data.find((a) => a.isDefault);
      setDefaultAddress(def);

      // also sync selected address
      if (def?._id) setSelectedAddressId(def._id);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load addresses");
    }
  };

  const fetchWallet = async () => {
    try {
      setWalletLoading(true);
      const { data } = await axios.get(`/wallet/get-wallet-balance/${id}`);
      setWallet(data.data);
    } catch {
      toast.error("Failed to load wallet");
    } finally {
      setWalletLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading((p) => ({ ...p, category: true }));
      setError((p) => ({ ...p, category: false }));
      const { data } = await axios.get("/category/get-all?special=all");
      setCategories(data.data || []);
    } catch (e) {
      setError((p) => ({ ...p, category: true }));
      toast.error("Failed to load categories");
    } finally {
      setLoading((p) => ({ ...p, category: false }));
    }
  };

  // ─── Fetch Sub Categories ─────────────────────────────────────────────────
  useEffect(() => {
    if (!selected.category) return;
    const fetchSub = async () => {
      try {
        setLoading((p) => ({ ...p, sub: true }));
        setError((p) => ({ ...p, sub: false }));
        setSubCategories([]);
        setProducts([]);
        setVarieties([]);
        const { data } = await axios.get(
          `/sub-category/get-sub-category-by-category/${selected.category}`,
        );
        setSubCategories(data.data || []);
      } catch {
        setError((p) => ({ ...p, sub: true }));
        toast.error("Failed to load sub categories");
      } finally {
        setLoading((p) => ({ ...p, sub: false }));
      }
    };
    fetchSub();
  }, [selected.category]);

  // ─── Fetch Products ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!selected.subCategory) return;
    const fetchProduct = async () => {
      try {
        setLoading((p) => ({ ...p, product: true }));
        setError((p) => ({ ...p, product: false }));
        setProducts([]);
        setVarieties([]);
        const { data } = await axios.get(
          `/product/get-product-by-sub-category/${selected.subCategory}`,
        );
        setProducts(data.data || []);
      } catch {
        setError((p) => ({ ...p, product: true }));
        toast.error("Failed to load products");
      } finally {
        setLoading((p) => ({ ...p, product: false }));
      }
    };
    fetchProduct();
  }, [selected.subCategory]);

  // ─── Fetch Varieties ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!selected.product) return;
    const fetchVariety = async () => {
      try {
        setLoading((p) => ({ ...p, variety: true }));
        setError((p) => ({ ...p, variety: false }));
        setVarieties([]);
        const { data } = await axios.get(
          `/product-variety/get-productVarieties-by-product/${selected.product}`,
        );
        setVarieties(data.data || []);
      } catch {
        setError((p) => ({ ...p, variety: true }));
        toast.error("Failed to load varieties");
      } finally {
        setLoading((p) => ({ ...p, variety: false }));
      }
    };
    fetchVariety();
  }, [selected.product]);

  // ─── Fetch Questions when variety selected ────────────────────────────────
  useEffect(() => {
    if (!selected.variety) {
      setQuestions([]);
      setAnswers({});
      return;
    }
    const fetchQuestions = async () => {
      try {
        setQuestionsLoading(true);
        setQuestions([]);
        setAnswers({});
        const { data } = await axios.get(
          `/question/variety/${selected.variety}`,
        );
        const active = (data.data || []).filter((q) => q.isActive !== false);
        setQuestions(active);
      } catch {
        toast.error("Failed to load product questions");
      } finally {
        setQuestionsLoading(false);
      }
    };
    fetchQuestions();
  }, [selected.variety]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleAnswerChange = (questionId, questionText, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        questionId,
        question: questionText,
        answeredByBuyer: value,
      },
    }));
  };

  const handlePriceChange = (field, value) => {
    if (field === "MOQ") setPriceAndQuantity((p) => ({ ...p, MOQ: value }));
    else if (field === "currency")
      setPriceAndQuantity((p) => ({ ...p, currency: value }));
    else if (field === "priceMin")
      setPriceAndQuantity((p) => ({
        ...p,
        expectedPriceRange: { ...p.expectedPriceRange, min: value },
      }));
    else if (field === "priceMax")
      setPriceAndQuantity((p) => ({
        ...p,
        expectedPriceRange: { ...p.expectedPriceRange, max: value },
      }));
  };

  const handleDeliveryChange = (field, value) =>
    setDeliveryTime((p) => ({ ...p, [field]: value }));

  const handleImage = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 3)
      return toast.error("Max 3 images allowed");
    const oversized = files.find((f) => f.size > 10 * 1024 * 1024);
    if (oversized) {
      return toast.error("Each image must be less than 10 MB");
    }
    setImages((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const handleVideo = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (videos.length + files.length > 2) {
      return toast.error("Max 2 videos allowed");
    }
    const oversized = files.find((f) => f.size > 25 * 1024 * 1024);
    if (oversized) {
      return toast.error("Each video must be less than 25 MB");
    }

    setVideos((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const selectedVarietyData = varieties.find((v) => v._id === selected.variety);
  const floorPrice = selectedVarietyData?.floor_price;
  const capPrice = selectedVarietyData?.cap_price;
  const varietyMOQ =
    selectedVarietyData?.slabAdjustedMOQ ||
    selectedVarietyData?.slabAdjustedMOQ ||
    null;
  const standardizedUnit = selectedVarietyData?.standardizedUNIT || "Kg";
  const minPrice = Number(priceAndQuantity.expectedPriceRange.min);
  const maxPrice = Number(priceAndQuantity.expectedPriceRange.max);

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selected.category) return toast.error("Please select a category");
    if (!selected.subCategory)
      return toast.error("Please select a sub category");
    if (!selected.product) return toast.error("Please select a product");

    if (!isDeclarationAccepted) {
      return toast.error("Please accept the declaration to continue");
    }

    if (!isCustomOrder && !selected.variety) {
      return toast.error("Please select a product variety");
    }

    if (isCustomOrder && !customVarietyName.trim()) {
      return toast.error("Please enter custom variety name");
    }

    if (!priceAndQuantity.MOQ) {
      return toast.error("Please enter quantity");
    }

    if (
      !isCustomOrder &&
      varietyMOQ &&
      Number(priceAndQuantity.MOQ) < varietyMOQ
    ) {
      return toast.error(`Quantity must be at least ${varietyMOQ} ${standardizedUnit}`);
    }

    if (
      !priceAndQuantity.expectedPriceRange.min ||
      !priceAndQuantity.expectedPriceRange.max
    ) {
      return toast.error("Please enter expected price");
    }

    if (!isCustomOrder) {
      if (Number(minPrice) >= Number(maxPrice)) {
        return toast.error("Max price must be greater than min price");
      }

      const minimumAllowedMaxPrice = Number((minPrice * 1.1).toFixed(2));

      if (maxPrice < minimumAllowedMaxPrice) {
        return toast.error(
          `Maximum price must be at least 10% higher than the minimum price (₹${minimumAllowedMaxPrice})`
        );
      }

      if (floorPrice && minPrice < floorPrice) {
        return toast.error(`Minimum price must be at least ₹${floorPrice}`);
      }

      if (capPrice && maxPrice > capPrice) {
        return toast.error(`Maximum price cannot exceed ₹${capPrice}`);
      }
    }
    if (!deliveryTime.fromDate || !deliveryTime.toDate)
      return toast.error("Please select delivery time range");
    if (new Date(deliveryTime.toDate) <= new Date(deliveryTime.fromDate))
      return toast.error("Delivery end date must be after start date");
    if (!selectedAddressId)
      return toast.error("Please select a delivery address");
    if (images.length === 0) return toast.error("At least 1 image is required");
    if (videos.length === 0) return toast.error("At least 1 video is required");

    try {
      setSubmitting(true);
      setAppLoading((p) => ({ ...p, uploading: true }));

      const uploadedImages = await Promise.all(
        images.map((img) => s3Upload(img, "requirements/images")),
      );
      const uploadedVideos = await Promise.all(
        videos.map((vid) => s3Upload(vid, "requirements/videos")),
      );
      setAppLoading((p) => ({ ...p, uploading: false }));

      const relatedQuestionsAndAnswers = isCustomOrder
        ? []
        : Object.values(answers).filter(
          (a) => a.answeredByBuyer && a.answeredByBuyer.trim() !== "",
        );
      const otherReqsPayload = otherRequirements
        .filter((r) => r.trim() !== "")
        .map((r) => ({ buyersRequirement: r }));

      const payload = {
        category: selected.category,
        subCategory: selected.subCategory,
        product: selected.product,

        ...(isCustomOrder
          ? { customVarietyName: customVarietyName.trim() }
          : { productVariety: selected.variety }),

        isThisCustomOrder: isCustomOrder,
        specification: {
          relatedQuestionsAndAnswers,
          otherRequirements: otherReqsPayload,
        },
        referencePhotoAndVideo: {
          images: uploadedImages.map((i) => i.url),
          videos: uploadedVideos.map((v) => v.url),
        },
        priceAndQuantity: {
          MOQ: Number(priceAndQuantity.MOQ),
          currency: priceAndQuantity.currency,
          standardizedUnit: standardizedUnit,
          expectedPriceRange: {
            message: `Per ${standardizedUnit} Price`,
            min: Number(priceAndQuantity.expectedPriceRange.min),
            max: Number(priceAndQuantity.expectedPriceRange.max),
          },
        },
        expectedDeliveryTime: {
          fromDate: deliveryTime.fromDate,
          toDate: deliveryTime.toDate,
        },
        address: selectedAddressId,
        ...(offerId && { offerId }),
      };

      if (!wallet || wallet.availableBalance < PLATFORM_FEE) {
        setShowWalletModal(true);
        return toast.error("Insufficient wallet balance");
      }

      setAppLoading((p) => ({ ...p, submitting: true }));
      setPendingPayload(payload);
      setShowOTPModal(true);
    } catch (err) {
      toast.dismiss("upload");
      toast.error(
        err?.response?.data?.message || "Submit failed. Please try again.",
      );
    } finally {
      setSubmitting(false);
      setAppLoading({
        uploading: false,
        submitting: false,
        creatingOrder: false,
      });
    }
  };

  const createOrderAfterOTP = async () => {
    try {
      setSubmitting(true);
      setAppLoading((p) => ({ ...p, creatingOrder: true }));

      // debit first
      await axios.post("/transaction/create-debit-transaction", {
        category: "platform_fee",
        amount: PLATFORM_FEE,
        description: "Platform fee for posting requirement",
        platformService: "requirement_post",
      });

      // create order
      const res = await axios.post("/order/create-order", pendingPayload);
      const createdOrder = res.data.data;

      try {
        await axios.post("/declaration", {
          reason: "Requirement Posting",
          orderId: createdOrder._id,
          declaration_contents: [
            {
              content: "I confirm that all requirement details are accurate.",
            },
            {
              content:
                "I agree that this request is genuine and intended for business purposes.",
            },
            {
              content:
                "I accept platform terms and responsibility for this requirement.",
            },
          ],
          date: new Date(),
          e_sign: User?.e_signature,
        });
      } catch (err) {
        console.error("Requirement declaration failed:", err);
      }

      toast.success("Requirement posted successfully!");
      sessionStorage.removeItem(STORAGE_KEY);
      setSuccess(true);
      setIsDeclarationAccepted(false);
      setSearchParams("");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Submit failed");
    } finally {
      setSubmitting(false);
      setAppLoading({
        uploading: false,
        submitting: false,
        creatingOrder: false,
      });
      setPendingPayload(null);
    }
  };

  const resetOrderModeData = () => {
    setCustomVarietyName("");
    setAnswers({});
    setOtherRequirements([]);
    setPriceAndQuantity({
      MOQ: "",
      currency: "INR",
      expectedPriceRange: { min: "", max: "" },
    });
  };

  // ─── Success Screen ───────────────────────────────────────────────────────
  if (success)
    return (
      <div className="min-h-[60vh] flex flex-col justify-center items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center text-4xl">
          🎉
        </div>
        <h2 className="text-2xl font-bold text-primaryColor">
          Requirement Posted!
        </h2>
        <p className="text-gray-500 text-sm">
          Your requirement has been submitted successfully.
        </p>
        <div className="flex items-center gap-4 justify-center w-fit mx-auto">
          <button
            onClick={() => {
              setSuccess(false);
              setStep(0);
              setSelected({});
              setCustomVarietyName("");
              setIsCustomOrder(false);
              setImages([]);
              setVideos([]);
              setAnswers({});
              setOtherRequirements([]);
              setSampleRequested("No");
              setPriceAndQuantity({
                MOQ: "",
                currency: "INR",
                expectedPriceRange: { min: "", max: "" },
              });
              setDeliveryTime({ fromDate: "", toDate: "" });
              setSelectedAddressId("");
              sessionStorage.removeItem(STORAGE_KEY);
            }}
            className="mt-2 px-6 py-2.5 bg-primaryColor text-white rounded-xl font-semibold text-sm hover:bg-primaryColor/90 transition"
          >
            Post Another
          </button>
          <button
            onClick={() => navigate("/orders")}
            className="mt-2 px-6 py-2.5 bg-primaryColor text-white rounded-xl font-semibold text-sm hover:bg-primaryColor/90 transition"
          >
            View My Requirements
          </button>
        </div>
      </div>
    );

  // console.log(getUser())

  return (
    <>
      {(appLoading.uploading ||
        appLoading.submitting ||
        appLoading.creatingOrder) && (
          <AppLoader
            blocking
            message={
              appLoading.uploading ? " " : appLoading.creatingOrder ? " " : " "
            }
          />
        )}

      <div className="2xl:max-w-7xl mx-auto px-4 md:px-8 py-6">
        <StepIndicator currentStep={step} />

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_260px] gap-5">
          {/* LEFT */}
          {/* Mobile View */}
          <div className="lg:hidden">
            <MobileCollapse title="📦 Your Selection" defaultOpen>
              <ProductSummaryPanel
                selected={selected}
                categories={categories}
                subCategories={subCategories}
                products={products}
                varieties={varieties}
                isCustomOrder={isCustomOrder}
                customVarietyName={customVarietyName}
              />
            </MobileCollapse>
          </div>

          {/* Desktop View */}
          <div className="hidden lg:block">
            <ProductSummaryPanel
              selected={selected}
              categories={categories}
              subCategories={subCategories}
              products={products}
              varieties={varieties}
              isCustomOrder={isCustomOrder}
              customVarietyName={customVarietyName}
            />
          </div>

          {/* CENTER */}
          <div className="bg-white border h-fit border-gray-200 rounded-md p-3 md:p-6 shadow-sm space-y-6">
            {/* Header Row */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-sm md:text-base font-semibold text-gray-700">
                Ask For A Quick Quotation
              </h2>

              <button
                onClick={() => {
                  sessionStorage.removeItem(STORAGE_KEY);

                  setSelected({});
                  setCustomVarietyName("");
                  setIsCustomOrder(false);
                  setAnswers({});
                  setOtherRequirements([]);
                  setPriceAndQuantity({
                    MOQ: "",
                    currency: "INR",
                    expectedPriceRange: { min: "", max: "" },
                  });
                  setDeliveryTime({ fromDate: "", toDate: "" });
                  setSelectedAddressId("");
                  setStep(0);
                  setSearchParams({});
                  toast.success("Draft cleared");
                }}
                className="
        px-3 py-1.5
        text-xs md:text-sm
        font-semibold
        rounded-lg
        border border-red-200
        text-red-600
        bg-red-50
        hover:bg-red-100
        active:scale-95
        transition-all
      "
              >
                Clear All
              </button>
            </div>
            {step === 0 && (
              <>
                {/* ── Hierarchy selectors — hidden when coming from external navigation ── */}
                {!isLocked && (
                  <>
                    {/* Category */}
                    <div>
                      <SectionHeader title="Category" />
                      <div className="flex gap-2 flex-wrap">
                        {loading.category ? (
                          Array(5)
                            .fill(0)
                            .map((_, i) => <SkeletonTag key={i} />)
                        ) : error.category ? (
                          <p className="text-xs text-red-500">
                            Failed to load.{" "}
                            <button
                              onClick={fetchCategories}
                              className="underline"
                            >
                              Retry
                            </button>
                          </p>
                        ) : categories.length === 0 ? (
                          <NoDataState label="categories" />
                        ) : (
                          categories.map((c) => (
                            <Tag
                              key={c._id}
                              label={c.name}
                              active={selected.category === c._id}
                              onClick={() =>
                                setSelected((prev) => ({
                                  ...prev,
                                  category: c._id,
                                  subCategory: null,
                                  product: null,
                                  variety: null,
                                }))
                              }
                            />
                          ))
                        )}
                      </div>
                    </div>

                    {/* Sub Category */}
                    {!selected.category ? (
                      <EmptyState
                        icon="☝️"
                        title="Select a category first"
                        subtitle="Sub categories will appear here once you pick a category above"
                      />
                    ) : (
                      <div>
                        <SectionHeader title="Select Sub - Category" />
                        <div className="flex gap-2 flex-wrap">
                          {loading.sub ? (
                            Array(4)
                              .fill(0)
                              .map((_, i) => <SkeletonTag key={i} />)
                          ) : error.sub ? (
                            <p className="text-xs text-red-500">
                              Failed to load sub categories.
                            </p>
                          ) : subCategories.length === 0 ? (
                            <NoDataState label="sub categories" />
                          ) : (
                            subCategories.map((c) => (
                              <Tag
                                key={c._id}
                                label={c.name}
                                active={selected.subCategory === c._id}
                                onClick={() =>
                                  setSelected({
                                    ...selected,
                                    subCategory: c._id,
                                    product: undefined,
                                    variety: undefined,
                                  })
                                }
                              />
                            ))
                          )}
                        </div>
                      </div>
                    )}

                    {/* Product */}
                    {selected.category &&
                      (!selected.subCategory ? (
                        <EmptyState
                          icon="👆"
                          title="Select a sub category"
                          subtitle="Products will appear once a sub category is selected"
                        />
                      ) : (
                        <div>
                          <SectionHeader title="Select Product" />
                          <div className="flex gap-2 flex-wrap">
                            {loading.product ? (
                              Array(4)
                                .fill(0)
                                .map((_, i) => <SkeletonTag key={i} />)
                            ) : error.product ? (
                              <p className="text-xs text-red-500">
                                Failed to load products.
                              </p>
                            ) : products.length === 0 ? (
                              <NoDataState label="products" />
                            ) : (
                              products.map((c) => (
                                <Tag
                                  key={c._id}
                                  label={c.name}
                                  active={selected.product === c._id}
                                  onClick={() =>
                                    setSelected({
                                      ...selected,
                                      product: c._id,
                                      variety: undefined,
                                    })
                                  }
                                />
                              ))
                            )}
                          </div>
                        </div>
                      ))}

                    {/* Variety picker */}
                    {selected.subCategory &&
                      (!selected.product ? (
                        <EmptyState
                          icon="🎯"
                          title="Select a product"
                          subtitle="Product varieties will appear once a product is chosen"
                        />
                      ) : (
                        <div>
                          <SectionHeader title="Select Product Variety" />
                          <div className="flex flex-wrap gap-x-6 gap-y-3">
                            {isCustomOrder ? (
                              <div className="w-full">
                                <input
                                  type="text"
                                  placeholder="Enter custom variety name"
                                  value={customVarietyName}
                                  onChange={(e) =>
                                    setCustomVarietyName(e.target.value)
                                  }
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primaryColor focus:outline-none"
                                />
                              </div>
                            ) : (
                              <>
                                {loading.variety ? (
                                  Array(4)
                                    .fill(0)
                                    .map((_, i) => <SkeletonRadio key={i} />)
                                ) : error.variety ? (
                                  <p className="text-xs text-red-500">
                                    Failed to load varieties.
                                  </p>
                                ) : varieties.length === 0 ? (
                                  <NoDataState label="varieties" />
                                ) : (
                                  varieties.map((v) => (
                                    <label
                                      key={v._id}
                                      className="flex items-center gap-2 cursor-pointer group"
                                    >
                                      <div
                                        onClick={() =>
                                          setSelected({
                                            ...selected,
                                            variety: v._id,
                                          })
                                        }
                                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selected.variety === v._id ? "border-primaryColor" : "border-gray-300"}`}
                                      >
                                        {selected.variety === v._id && (
                                          <div className="w-2 h-2 rounded-full bg-primaryColor" />
                                        )}
                                      </div>
                                      <span
                                        onClick={() =>
                                          setSelected({
                                            ...selected,
                                            variety: v._id,
                                          })
                                        }
                                        className={`text-sm ${selected.variety === v._id ? "text-primaryColor font-medium" : "text-gray-600"}`}
                                      >
                                        {v.varietyName}
                                      </span>
                                    </label>
                                  ))
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                  </>
                )}

                {/* ── After variety selected: Product Details + all new sections ── */}
                {(selected.variety || isCustomOrder) && (
                  <>
                    {/* Selected Variety Summary */}
                    {!isCustomOrder && selectedVarietyData && (
                      <div className="border-t border-gray-100 pt-4">
                        <SectionHeader title="Selected Variety" />
                        <SelectedVarietyCard variety={selectedVarietyData} />
                      </div>
                    )}

                    {/* Product Details / Questionnaire */}
                    <div className="border-t border-gray-100 pt-4">
                      <SectionHeader title="Product Details" />
                      {!isCustomOrder && (
                        <QuestionnaireSection
                          questions={questions}
                          loading={questionsLoading}
                          answers={answers}
                          onAnswerChange={handleAnswerChange}
                        />
                      )}
                    </div>

                    {/* Other Requirements */}
                    <div className="border-t border-gray-100 pt-4">
                      <OtherRequirements
                        requirements={otherRequirements}
                        onChange={setOtherRequirements}
                      />
                    </div>
                    {/* MOQ + Price */}
                    <div className="border-t border-gray-100 pt-4">
                      <CollapsibleSection
                        title={
                          isCustomOrder
                            ? "Order Quantity"
                            : "MOQ ( Minimum Order Quantity )"
                        }
                      >
                        <div className="pt-2">
                          <PriceAndQuantity
                            varietyMOQ={!isCustomOrder ? varietyMOQ : null}
                            floorPrice={!isCustomOrder ? floorPrice : null}
                            capPrice={!isCustomOrder ? capPrice : null}
                            priceAndQuantity={priceAndQuantity}
                            onChange={handlePriceChange}
                            standardizedUnit={standardizedUnit}
                          />
                        </div>
                      </CollapsibleSection>
                    </div>

                    {/* Delivery Time */}
                    <CollapsibleSection title="Expected Delivery Time Range">
                      <div className="pt-2">
                        <DeliveryTime
                          deliveryTime={deliveryTime}
                          onChange={handleDeliveryChange}
                        />
                      </div>
                    </CollapsibleSection>

                    {/* Address */}
                    <CollapsibleSection title="Select Delivery Address">
                      <div className="pt-2 max-w-[34rem] mx-auto">
                        {defaultAddress ? (
                          <div
                            onClick={() => setShowAddressModal(true)}
                            className="flex items-center justify-between gap-3 p-3 rounded-xl bg-primaryColor text-white cursor-pointer hover:bg-primaryColor/90 transition"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white/70">
                                Delivering to
                              </p>
                              <p className="text-sm font-semibold truncate">
                                {defaultAddress.addressLine1}
                              </p>
                              <p className="text-xs text-white/70">
                                {defaultAddress.city}, {defaultAddress.state}
                              </p>
                            </div>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditAddress(defaultAddress);
                                setAddressType(defaultAddress.addressType);
                                setShowAddModal(true);
                              }}
                              className="text-white/80 hover:text-white"
                            >
                              <PenLineIcon />
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => setShowAddressModal(true)}
                            className="border-2 border-dashed border-primaryColor/40 p-4 rounded-xl text-center cursor-pointer hover:bg-primaryColor/5"
                          >
                            <p className="text-sm font-semibold text-primaryColor">
                              + Add Delivery Address
                            </p>
                          </div>
                        )}
                      </div>
                    </CollapsibleSection>
                  </>
                )}

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isLocked) {
                        if (!selected.category)
                          return toast.error("Please select a category");
                        if (!selected.product)
                          return toast.error("Please select a product");
                      }
                      if (!isCustomOrder && !selected.variety) {
                        return toast.error("Please select variety");
                      }
                      if (isCustomOrder && !customVarietyName.trim()) {
                        return toast.error("Please enter custom variety");
                      }
                      setStep(1);
                    }}
                    className="w-full bg-primaryColor text-white py-3.5 rounded-xl font-semibold text-sm hover:bg-primaryColor/90 transition-all active:scale-[0.98] shadow-md shadow-primaryColor/25"
                  >
                    Continue
                  </button>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <CollapsibleSection title="Reference Photo & Video">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                    <div>
                      <UploadBox
                        type="image"
                        onChange={handleImage}
                        disabled={images.length >= 3}
                        count={images.length}
                        max={3}
                      />
                      <ImagePreview
                        files={images}
                        onRemove={(i) =>
                          setImages((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                      />
                    </div>
                    <div>
                      <UploadBox
                        type="video"
                        onChange={handleVideo}
                        disabled={videos.length >= 2}
                        count={videos.length}
                        max={2}
                      />
                      <VideoPreview
                        files={videos}
                        onRemove={(i) =>
                          setVideos((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <svg
                      className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    At least 1 image and 1 video are required. You can upload up
                    to 3 images and 2 videos.
                  </div>
                </CollapsibleSection>

                <div className="border border-primaryColor/20 bg-primaryColor/5 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-primaryColor">
                    Platform Fee Required
                  </p>

                  <p className="text-xs text-gray-600">
                    A platform fee of{" "}
                    <span className="font-semibold">₹{PLATFORM_FEE}</span> will
                    be deducted from your wallet to post this requirement.
                  </p>
                  <p className="text-xs text-gray-600">
                    This small platform fee helps us verify serious buyers,
                    maintain secure systems, and ensure fair pricing from
                    verified suppliers.
                  </p>

                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">Wallet Balance</p>
                    <p className="font-bold text-primaryColor">
                      ₹{wallet?.availableBalance?.toLocaleString("en-IN") || 0}
                    </p>
                  </div>

                  {wallet && wallet.availableBalance < PLATFORM_FEE && (
                    <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <span className="text-xs text-red-600">
                        Insufficient balance
                      </span>
                      <button
                        onClick={() => setShowWalletModal(true)}
                        className="text-xs font-semibold text-primaryColor underline"
                      >
                        Add Money
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-2 mt-4">
                  <input
                    type="checkbox"
                    id="declaration"
                    checked={isDeclarationAccepted}
                    onChange={(e) => setIsDeclarationAccepted(e.target.checked)}
                    className="mt-1 cursor-pointer"
                  />

                  <label
                    htmlFor="declaration"
                    className="text-xs text-gray-600 leading-relaxed cursor-pointer"
                  >
                    I confirm that all the requirement details provided are true
                    and accurate. I understand that submitting false information
                    may lead to account action.
                  </label>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setStep(0)}
                    className="flex-1 border-2 border-gray-200 text-gray-600 py-3.5 rounded-xl font-medium text-sm hover:border-primaryColor hover:text-primaryColor transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11 17l-5-5 5-5M18 12H6"
                      />
                    </svg>
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || !isDeclarationAccepted}
                    className="flex-1 bg-primaryColor text-white py-3.5 rounded-xl font-semibold text-sm hover:bg-primaryColor/90 transition-all active:scale-[0.98] shadow-md shadow-primaryColor/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <svg
                          className="animate-spin w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* RIGHT */}
          {/* Mobile View */}
          <div className="lg:hidden">
            <MobileCollapse title="📘 How to Use">
              <HowToUseSidebar />
            </MobileCollapse>
          </div>

          {/* Desktop View */}
          <div className="hidden lg:block">
            <HowToUseSidebar />
          </div>
        </div>
      </div>

      {/* Modals */}

      <AddressSelectModal
        showModal={showAddressModal}
        closeModal={() => setShowAddressModal(false)}
        addresses={addresses}
        setAddresses={setAddresses}
        setDefaultAddress={setDefaultAddress}
        refreshAddresses={fetchAddresses}
        openAddModal={(type, data) => {
          setAddressType(type);
          setEditAddress(data || null);

          setShowAddressModal(false);
          setTimeout(() => setShowAddModal(true), 250);
        }}
      />
      {isLoaded && (
        <AddAddressModalAPI
          showModal={showAddModal}
          closeModal={() => setShowAddModal(false)}
          refreshAddresses={fetchAddresses}
          type={addressType}
          editData={editAddress}
          addresses={addresses}
        />
      )}

      <RechargeModal
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        wallet={wallet}
        user={user}
        onSuccess={fetchWallet}
      />

      <OTPModal
        show={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        onVerified={createOrderAfterOTP}
        identifier={getUser()?.phone}
      />
    </>
  );
};

export default PostRequirement;
